import React, { useState, useEffect, useRef } from 'react';
import { useNavigation } from 'context/NavigationContext';
import { BringzzPageContainer, BringzzIcon, BringzzText, BringzzInput, BringzzBottomDrawer } from "@bringzz/components";
import useUser from 'hooks/useUser';
import useRequest from 'hooks/useRequest';
import { useParams } from 'react-router-dom';
import TextMessage from "components/TextMessage";
import classnames from 'classnames';
import { useSocket } from 'context/SocketClusterContext';
import { useTranslation } from 'react-i18next';

const ConversationPage = () => {
    const { backPress, navigate } = useNavigation();
    const { sendMessage, onRefetchData } = useSocket();
    const { t } = useTranslation();

    const [searchQuery, setSearchQuery] = useState("");
    const [showPharmacyDrawer, setShowPharmacyDrawer] = useState(false);
    const [showArchiveDeleteDrawer, setShowArchiveDeleteDrawer] = useState(false);

    const { getUser } = useUser();
    const user = getUser();
    const { sendRequest, data, error, loading } = useRequest();
    const [chat, setChat] = useState(null)
    const [chatContent, setChatContent] = useState([])
    const [message, setMessage] = useState()
    const [isLoadingChat, setIsLoadingChat] = useState(true)
    const isInitialLoad = useRef(true); // Track if this is the first load

    const { id: recipientId } = useParams(); // Route param is recipient_id

    const lastMessageRef = useRef(null);

    const scrollToLastMessage = () => {
        if (lastMessageRef.current) {
            lastMessageRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        scrollToLastMessage();
    }, [chatContent]);

    const sendText = async () => {
        if (message.trim().length !== 0 && chat?.id) {
            const messageToSend = message;

            // Immediately clear input and add optimistic message
            setMessage("");

            // Add optimistic message to chat
            const optimisticMessage = {
                text_content: messageToSend,
                created_by_id: user.id,
                created: new Date().toISOString().split('T')[0] + ' ' + new Date().toTimeString().split(' ')[0],
                pending: true // Mark as pending
            };
            setChatContent(prev => [...prev, optimisticMessage]);

            await sendRequest({
                method: 'POST',
                url: `/module/chat/sendText`,
                data: {
                    "chatId": chat.id,
                    "textContent": messageToSend,
                    "userId": user.id
                }
            })
                .then(response => {
                    if (response.status === "success") {
                        setChatContent(response.data)

                        // Notify recipient via socket
                        sendMessage(chat?.recipient.id, {
                            content: messageToSend,
                            type: 'chat_message',
                            chatId: chat.id
                        })
                    }
                })
                .catch(error => {
                    console.log(error);
                    // On error, remove the optimistic message
                    setChatContent(prev => prev.filter(msg => !msg.pending));
                });
        }
    };

    useEffect(() => {
        const fetchChat = async () => {
            // Only show loading state on initial load
            if (isInitialLoad.current) {
                setIsLoadingChat(true);
            }

            // Get or create chat using recipient_id
            await sendRequest({
                method: 'POST',
                url: `/module/chat/getChat`,
                data: { recipient_id: recipientId, user_id: user.id }
            })
                .then(async (response) => {
                    if (response.status === "success") {
                        console.log(response);
                        setChat(response.data)

                        // Now fetch chat content using the chat id
                        await sendRequest({
                            method: 'POST',
                            url: `/module/chat/getChatContent`,
                            data: { chat_id: response.data.id }
                        })
                            .then(response => {
                                if (response.status === "success") {
                                    console.log(response);
                                    setChatContent(response.data)
                                }
                            })
                            .catch(error => {
                                console.log(error);
                            })
                            .finally(() => {
                                if (isInitialLoad.current) {
                                    setIsLoadingChat(false);
                                    isInitialLoad.current = false; // Mark initial load as complete
                                }
                            });
                    }
                })
                .catch(error => {
                    console.log(error);
                    if (isInitialLoad.current) {
                        setIsLoadingChat(false);
                        isInitialLoad.current = false;
                    }
                });
        }

        fetchChat()

        const cleanupRefetchData = onRefetchData((data) => {
            // Refetch chat content when new messages arrive
            fetchChat()
        });

        return () => {
            if (cleanupRefetchData) {
                cleanupRefetchData();
            }
        };
    }, [onRefetchData])

    const handleMessageChange = (e) => {
        setMessage(e.target.value);
    };

    const handlePharmacyClick = () => {
        // Only show drawer if recipient is a pharmacist (userRole === 1)
        if (chat?.recipient?.userRole === 1) {
            setShowPharmacyDrawer(true);
        }
    };

    const handleViewProfile = async () => {
        console.log('handleViewProfile called with recipient ID:', chat?.recipient?.id);
        try {
            const response = await sendRequest({
                method: 'POST',
                url: `/module/chat/getPharmacyIntegrationId`,
                data: { user_id: chat?.recipient?.id },
            });

            console.log('getPharmacyIntegrationId response:', response);

            if (response.status === 'success') {
                console.log('Navigating to pharmacy profile:', response.data);
                navigate(`/pharmacy/profile/${response.data}`);
            } else {
                console.error('Could not get pharmacy integration ID:', response.message);
            }
        } catch (error) {
            console.error('Error getting pharmacy integration ID:', error);
        }
        setShowPharmacyDrawer(false);
    };

    const handleCall = () => {
        if (chat?.recipient?.phoneNumber) {
            window.location.href = `tel:${chat.recipient.phoneNumber}`;
        }
        setShowPharmacyDrawer(false);
    };

    const handleArchive = async () => {
        try {
            await sendRequest({
                method: 'POST',
                url: `/module/chat/toggleArchive`,
                data: { chatId: chat.id, user_id: user.id },
            });
            setShowArchiveDeleteDrawer(false);
            setShowPharmacyDrawer(false);
            navigate('/settings/chats');
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = async () => {
        try {
            await sendRequest({
                method: 'DELETE',
                url: `/module/chat/deleteChat`,
                data: { chat_id: chat.id, user_id: user.id },
            });
            setShowArchiveDeleteDrawer(false);
            setShowPharmacyDrawer(false);
            navigate('/settings/chats');
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <BringzzPageContainer
            footerHover={true}
            headerHover={true}
            header={{
                left: <BringzzIcon
                    size="22"
                    icon="IconChevronLeft"
                    folder="LineIcons"
                    className="cursor-pointer"
                    onClick={() => backPress()}
                />,
                title: <BringzzText tag='h2' className='text-center whitespace-nowrap'>{chat?.recipient && `${chat.recipient.firstName} ${chat.recipient.lastName}`}</BringzzText>,
            }}
            footer={
                <div className='p-4 flex space-x-2'>
                    <div className='relative w-full'>
                        <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                            <BringzzIcon
                                size="20"
                                icon="IconSend"
                                folder="LineIcons"
                                className="cursor-pointer"
                                onClick={() => {
                                    sendText()
                                }}
                            />
                        </div>
                        <textarea
                            className='shadow-xl border-slate-200 border bg-gray-100 p-3 outline-none text-midnight-blue w-full rounded-md placeholder:text-gray-300 placeholder:text-sm flex items-center text-sm pr-10'
                            placeholder='Type your message...'
                            type='textarea'
                            rows={1}
                            value={message}
                            onChange={handleMessageChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendText();
                                }
                            }}
                        ></textarea>
                        {/* <BringzzInput
                            className='shadow-xl border-slate-200 border bg-gray-100'
                            placeholder='Type your message...'
                            type='textarea'
                            onChange={(e, newIsValid, newValue) => setMessage(newValue)}
                            icon={{
                                right: <BringzzIcon
                                    size="20"
                                    icon="IconSend"
                                    folder="LineIcons"
                                    className="cursor-pointer"
                                    onClick={() => {
                                        sendMessage()
                                    }}
                                />,
                            }}
                        /> */}
                    </div>
                </div>
            }
        >
            <div className="flex flex-col w-full pt-12 pb-40 md:pb-12">
                {/* Conversation content */}
                <div
                    className="flex flex-col flex-1 p-4 scroll-smooth overflow-y-scroll overflow-x-hidden justify-end"
                >
                    {isLoadingChat ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-midnight-blue"></div>
                                <p className="mt-2 text-sm text-gray-500">Loading conversation...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="">
                            {[...chatContent]
                                .sort((a, b) => {
                                    // Parse DD.MM.YYYY HH:MM format
                                    const parseDate = (dateStr) => {
                                        const [datePart, timePart] = dateStr.split(' ');
                                        const [day, month, year] = datePart.split('.');
                                        return new Date(`${year}-${month}-${day}T${timePart}`);
                                    };
                                    return parseDate(a.created) - parseDate(b.created);
                                })
                                .map((message, index, sortedMessages) => {
                                    const isSender = message.created_by_id == user.id;
                                    const hasPrevious =
                                        index !== 0 ??
                                        sortedMessages[index - 1].created_by_id !== message.created_by_id
                                    const messageSide = isSender ? "items-end" : "items-start";

                                    const differentDate =
                                        index !== 0 &&
                                        sortedMessages[index].created.split(' ')[0] !==
                                        sortedMessages[index - 1].created.split(' ')[0]

                                    const isLastMessage = index === sortedMessages.length - 1;

                                    return (
                                        <div key={index} ref={isLastMessage ? lastMessageRef : null}>
                                            <div
                                                className={classnames(
                                                    "items-center justify-center my-3 -mx-6",
                                                    differentDate ? "flex" : "hidden"
                                                )}
                                            >
                                                <div className="flex-auto border-b"></div>
                                                <div className="flex-0 mx-4 text-xs text-slate-500">
                                                    {message.created}
                                                </div>
                                                <div className="flex-auto border-b"></div>
                                            </div>

                                            <div
                                                className={classnames(
                                                    hasPrevious ? "mt-1" : "mt-3",
                                                    messageSide,
                                                    "flex flex-col",
                                                    message.pending && "opacity-50 transition-opacity"
                                                )}
                                            >
                                                <TextMessage
                                                    message={{
                                                        text: message.text_content,
                                                        date: message.created,
                                                    }}
                                                    isSender={isSender}
                                                    hasPrevious={false}
                                                    isPending={message.pending || false}
                                                    isRead={message.is_read || false}
                                                    onPharmacyClick={!isSender && chat?.recipient?.userRole === 1 ? handlePharmacyClick : null}
                                                    initials={isSender
                                                        ? (user.initials || `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`)
                                                        : (chat?.recipient?.initials || `${chat?.recipient?.firstName?.[0] || ''}${chat?.recipient?.lastName?.[0] || ''}`)}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>
            </div>

            <BringzzBottomDrawer
                isOpen={showPharmacyDrawer}
                close={() => setShowPharmacyDrawer(false)}
                title={chat?.recipient ? `${chat.recipient.firstName} ${chat.recipient.lastName}` : 'Pharmacy'}
            >
                <div className="px-6 py-4">
                    <div className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer mb-2" onClick={handleViewProfile}>
                        <BringzzIcon icon="IconUser" folder="LineIcons" size="20" className="mr-3" />
                        <BringzzText>{t('component.chat.view_profile')}</BringzzText>
                    </div>

                    <div className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer mb-2" onClick={handleCall}>
                        <BringzzIcon icon="IconPhone" folder="LineIcons" size="20" className="mr-3" />
                        <BringzzText>{t('component.chat.call_pharmacy')}</BringzzText>
                    </div>

                    <div className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer mb-2" onClick={() => setShowArchiveDeleteDrawer(true)}>
                        <BringzzIcon icon="IconSettings" folder="LineIcons" size="20" className="mr-3" />
                        <BringzzText>{t('component.chat.archive_delete_chat')}</BringzzText>
                    </div>
                </div>
            </BringzzBottomDrawer>

            <BringzzBottomDrawer
                isOpen={showArchiveDeleteDrawer}
                close={() => setShowArchiveDeleteDrawer(false)}
                title={t('component.chat.chat_actions')}
            >
                <div className="px-6 py-4">
                    <div className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer mb-2" onClick={handleArchive}>
                        <BringzzIcon icon="IconArchive" folder="LineIcons" size="20" className="mr-3" />
                        <BringzzText>{t('component.chat.archive_chat')}</BringzzText>
                    </div>

                    <div className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer text-red-600" onClick={handleDelete}>
                        <BringzzIcon icon="IconTrashCan" folder="LineIcons" size="20" className="mr-3 text-red-600" />
                        <BringzzText className="text-red-600">{t('component.chat.delete_chat')}</BringzzText>
                    </div>
                </div>
            </BringzzBottomDrawer>
        </BringzzPageContainer>
    )
}

export default ConversationPage;